import { useState, useEffect, useRef } from "react";
import { Package, Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const emptyProduct = { name: "", slug: "", description: "", short_description: "", price: 180, sale_price: "", weight: "50g", image: "", category: "popular", flavor_profile: "", nutritional_benefits: [], best_dishes: [], storage_tips: "", in_stock: true, featured: false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { getToken } = useAuth();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers });
      setProducts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyProduct); setUploading(false); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm(p); setUploading(false); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing}`, form, { headers });
        toast.success("Product updated");
      } else {
        const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
        await axios.post(`${API}/admin/products`, { ...form, slug }, { headers });
        toast.success("Product created");
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers });
      toast.success("Deleted");
      fetchProducts();
    } catch (e) { toast.error("Delete failed"); }
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${window.location.origin}${res.data.url}`;
      updateForm("image", fullUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Products ({products.length})</h1>
        <Button onClick={openCreate} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-200 animate-pulse rounded-lg" />)}</div>
      ) : (
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-medium text-gray-500">Image</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Name</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Short Description</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Price</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Weight</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Stock</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Featured</th>
                    <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`product-row-${p.slug}`}>
                      <td className="px-4 py-2">
                        <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                      </td>
                      <td className="px-4 py-2 font-medium text-gray-800">{p.name}</td>
                      <td className="px-4 py-2 text-gray-500 max-w-[180px] truncate">{p.short_description || "—"}</td>
                      <td className="px-4 py-2 whitespace-nowrap">₹{p.price}</td>
                      <td className="px-4 py-2">{p.weight || "50g"}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs capitalize">{p.category}</span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.in_stock ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {p.in_stock ? "In Stock" : "Out"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.featured ? "bg-amber-100 text-amber-700" : "text-gray-400"}`}>
                          {p.featured ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(p.id)} data-testid={`delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => updateForm("name", e.target.value)} data-testid="product-name-input" /></div>
            <div><Label>Short Description</Label><Input value={form.short_description} onChange={e => updateForm("short_description", e.target.value)} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => updateForm("description", e.target.value)} rows={3} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>MRP (₹)</Label><Input type="number" value={form.price} onChange={e => updateForm("price", Number(e.target.value))} /></div>
              <div><Label>Sale Price (₹)</Label><Input type="number" value={form.sale_price} onChange={e => updateForm("sale_price", Number(e.target.value))} /></div>
              <div><Label>Weight</Label><Input value={form.weight} onChange={e => updateForm("weight", e.target.value)} /></div>
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="mt-1">
                {form.image ? (
                  <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    <img src={form.image} alt="Preview" className="w-full h-40 object-cover" />
                    <div
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <span className="text-white text-sm font-medium">Change Image</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg h-40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-praroha-sage hover:bg-praroha-sage/5 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="image-upload-area"
                  >
                    {uploading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-praroha-sage border-t-transparent" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-500">Click to upload image</span>
                        <span className="text-xs text-gray-400">JPG, PNG, WebP</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="image-file-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.category} onChange={e => updateForm("category", e.target.value)}>
                  <option value="popular">Popular</option>
                  <option value="herbs">Herbs</option>
                  <option value="specialty">Specialty</option>
                  <option value="wellness">Wellness</option>
                </select>
              </div>
              <div><Label>Flavor Profile</Label><Input value={form.flavor_profile} onChange={e => updateForm("flavor_profile", e.target.value)} /></div>
            </div>
            <div><Label>Nutritional Benefits (comma-separated)</Label><Input value={Array.isArray(form.nutritional_benefits) ? form.nutritional_benefits.join(", ") : form.nutritional_benefits || ""} onChange={e => updateForm("nutritional_benefits", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="e.g. Rich in Vitamin C, High in Iron" /></div>
            <div><Label>Best Dishes (comma-separated)</Label><Input value={Array.isArray(form.best_dishes) ? form.best_dishes.join(", ") : form.best_dishes || ""} onChange={e => updateForm("best_dishes", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="e.g. Salads, Smoothies, Sandwiches" /></div>
            <div><Label>Storage Tips</Label><Input value={form.storage_tips || ""} onChange={e => updateForm("storage_tips", e.target.value)} placeholder="e.g. Keep refrigerated. Use within 5 days." /></div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={e => updateForm("in_stock", e.target.checked)} /> In Stock</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={e => updateForm("featured", e.target.checked)} /> Featured</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="save-product-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
